import { defineStore } from "pinia";
import { effectScope, onScopeDispose, reactive, ref, watch } from "vue";
import api from "@/api";
import type { ProjectAccessVerdict, ProjectAccessVerdictCan } from "@/api/index.d";
import { backoffDelayMs } from "@common-ui/utilities/retry";
import { logWarn } from "@/helpers/logError";
import UnauthenticatedError from "@/types/errors/UnauthenticatedError";
import { useAuthSessionStore } from "@/stores/authSession";

export type ProjectVerdictStatus = "loading" | "ready" | "unavailable";

const CAN_KEYS: Array<keyof ProjectAccessVerdictCan> = [
	"view", "addContent", "editContent", "ownUploadsOnly", "editDetails", "manageChecklists", "editChecklistItems",
	"manageAccess", "readRoster", "delete", "requestReview", "assignSessions", "requestEditAccess"
];
export function projectVerdictFailure(error: unknown): { status: number | undefined; errorClass: string; reason: string } {
	const failure = typeof error === "object" && error !== null ? error as { httpStatus?: number; status?: number } : undefined;
	const status = failure?.httpStatus ?? failure?.status;
	// Class/status only: response messages can contain customer identifiers or email addresses.
	return { status, errorClass: error instanceof Error ? error.constructor.name : status ? "HTTPError" : "UnknownError",
		reason: status === 429 ? "rate limited" : "request failed" };
}
export function decodeProjectVerdict(value: unknown): ProjectAccessVerdict {
	if (!value || typeof value !== "object") throw new Error("malformed verdict");
	const source = value as ProjectAccessVerdict;
	if (typeof source.hasAccess !== "boolean" || typeof source.differentOrg !== "boolean" ||
		typeof source.isExternalEditor !== "boolean" || !source.can || typeof source.can !== "object" || Array.isArray(source.can)) {
		throw new Error("malformed verdict");
	}
	return {
		...source,
		differentOrg: source.differentOrg === true,
		isExternalEditor: source.isExternalEditor === true,
		can: Object.fromEntries(CAN_KEYS.map((key) => [key, source.can[key] === true])) as unknown as ProjectAccessVerdictCan
	};
}

export const useProjectVerdictsStore = defineStore("projectVerdicts", () => {
	const session = useAuthSessionStore();
	const entries = reactive(new Map<string, { verdict: ProjectAccessVerdict; fetchedAt: number; ttlMs: number }>());
	const inFlight = reactive(new Map<string, Promise<void>>());
	const expired = reactive(new Set<string>());
	const expirationTimers = new Map<string, ReturnType<typeof setTimeout>>();
	let retryTimer: ReturnType<typeof setTimeout> | undefined;
	let retryAt = 0;
	const retryPending = reactive(new Set<string>());
	const retryAttempts = new Map<string, number>();
	const failures = reactive(new Map<string, number>());
	const warned = new Set<string>();
	const errors = reactive(new Map<string, { at: number; reason: string }>());
	const lastError = ref<{ at: number; reason: string }>();
	const generations = new Map<string, number>();
	const epoch = ref(0);
	const keyFor = (id: string) => String(id).toLowerCase();
	function status(id: string): "fresh" | "stale" | "missing" | "error" {
		const key = keyFor(id);
		if (errors.has(key)) return "error";
		const entry = entries.get(key);
		if (!entry) return "missing";
		return !expired.has(key) && Date.now() - entry.fetchedAt < entry.ttlMs ? "fresh" : "stale";
	}
	function access(id: string): { verdict: ProjectAccessVerdict | undefined; verdictStatus: ProjectVerdictStatus; backgroundRetry: boolean; fetchedAt: number | undefined; generation: number; failures: number; transientFailure: boolean } {
		const key = keyFor(id);
		const entry = entries.get(key);
		const verdict = entry?.verdict;
		return { verdict, verdictStatus: verdict ? "ready" : errors.has(key) || !!entry ? "unavailable" : "loading",
			failures: failures.get(key) ?? 0, transientFailure: failures.has(key), backgroundRetry: retryPending.has(key), fetchedAt: entry?.fetchedAt, generation: generations.get(key) ?? 0 };
	}
	function verdict(id: string): ProjectAccessVerdict | undefined { return access(id).verdict; }
	function cancelRetry(key: string): void {
		retryPending.delete(key); retryAttempts.delete(key);
		if (!retryPending.size) { clearTimeout(retryTimer); retryTimer = undefined; retryAt = 0; }
	}
	function stopRetry(key: string): void {
		cancelRetry(key); failures.delete(key);
	}
	function scheduleRetry(key: string, rateLimited: boolean, retryAfterMs?: number): void {
		const pending = retryPending.has(key);
		if (pending && !rateLimited) return;
		const attempt = Math.max(0, (retryAttempts.get(key) ?? 0) - (pending ? 1 : 0));
		if (!pending) { retryAttempts.set(key, attempt + 1); retryPending.add(key); }
		const delay = Math.max(1000, retryAfterMs ?? 0, backoffDelayMs(Math.min(attempt, 5), rateLimited ? 5000 : 1000, 30000));
		const nextRetryAt = Date.now() + delay;
		// A later rate limit extends the shared wait; no pending key retries ahead of it.
		if (retryTimer !== undefined && nextRetryAt <= retryAt) return;
		clearTimeout(retryTimer); retryAt = nextRetryAt;
		retryTimer = setTimeout(() => {
			retryTimer = undefined; retryAt = 0;
			const keys = [...retryPending]; retryPending.clear();
			void ensure(keys);
		}, delay);
	}
	function removeCachedVerdict(key: string): void {
		clearTimeout(expirationTimers.get(key));
		expirationTimers.delete(key);
		expired.delete(key);
		entries.delete(key); stopRetry(key);
	}
	function cacheVerdict(key: string, verdict: ProjectAccessVerdict, ttlMs: number): void {
		removeCachedVerdict(key);
		// Publication advances identity even when two responses arrive in the same millisecond.
		generations.set(key, (generations.get(key) ?? 0) + 1);
		entries.set(key, { verdict, fetchedAt: Date.now(), ttlMs });
		stopRetry(key); warned.delete(key);
		// Date.now is not reactive. Expiry wakes the visible-id watcher for a quiet refresh.
		expirationTimers.set(key, setTimeout(() => {
			expirationTimers.delete(key);
			expired.add(key);
		}, ttlMs));
	}
	function invalidate(ids: string[]): void {
		for (const id of ids) {
			const key = keyFor(id);
			generations.set(key, (generations.get(key) ?? 0) + 1);
			removeCachedVerdict(key); stopRetry(key); warned.delete(key); errors.delete(key); inFlight.delete(key);
		}
	}
	function clear(): void {
		for (const timer of expirationTimers.values()) clearTimeout(timer);
		expirationTimers.clear(); expired.clear();
		clearTimeout(retryTimer); retryTimer = undefined; retryAt = 0; retryPending.clear(); retryAttempts.clear(); failures.clear(); warned.clear();
		epoch.value++; entries.clear(); errors.clear(); inFlight.clear(); generations.clear(); lastError.value = undefined;
	}
	async function ensure(ids: string[], single = false): Promise<void> {
		const unique = [...new Map(ids.filter(Boolean).map((id) => [keyFor(id), id])).values()];
		if (session.sessionLost) {
			// Retry cannot restore authentication. Settle visibly until authSession restores it.
			const failure = { at: Date.now(), reason: "session lost" };
			let shouldWarn = false;
			for (const id of unique) {
				const key = keyFor(id);
				removeCachedVerdict(key); stopRetry(key);
				errors.set(key, failure);
				if (!warned.has(key)) { warned.add(key); shouldWarn = true; }
			}
			lastError.value = failure;
			if (shouldWarn) logWarn("projectVerdicts: verdict unavailable", { reason: failure.reason });
			return;
		}
		const waiting = unique.map((id) => inFlight.get(keyFor(id))).filter((promise): promise is Promise<void> => !!promise);
		const pending = unique.filter((id) => status(id) !== "fresh" && !inFlight.has(keyFor(id)));
		for (let offset = 0; offset < pending.length; offset += 100) {
			const batch = pending.slice(offset, offset + 100);
			const requestEpoch = epoch.value;
			const versions = new Map(batch.map((id) => [keyFor(id), generations.get(keyFor(id)) ?? 0]));
			const current = (id: string) => requestEpoch === epoch.value && versions.get(keyFor(id)) === (generations.get(keyFor(id)) ?? 0);
			const request = (async () => {
				try {
					// The single-id GET unwraps only the verdict; the server issues one constant TTL (60 s) for
					// both endpoints, so the batch default below matches what the wrapper would have carried.
					const response = single && batch.length === 1
						? { verdicts: { [batch[0]]: await api.getProjectVerdict(batch[0]) }, ttlSeconds: 60 }
						: await api.getProjectVerdicts(batch);
					if (!response?.verdicts || typeof response.verdicts !== "object" || Array.isArray(response.verdicts)) throw Object.assign(new Error("malformed response"), { httpStatus: 400 });
					const ttlMs = Number.isFinite(response.ttlSeconds) && response.ttlSeconds > 0 ? response.ttlSeconds * 1000 : 60000;
					for (const id of batch) {
						if (!current(id)) continue;
						const key = keyFor(id);
						const raw = response.verdicts[id] ?? Object.entries(response.verdicts).find(([responseId]) => keyFor(responseId) === key)?.[1];
						if (raw === undefined) {
							removeCachedVerdict(key);
							const error = { at: Date.now(), reason: "missing id" };
							errors.set(key, error); lastError.value = error;
							logWarn("projectVerdicts: id missing from response", { id });
						} else {
							try {
								cacheVerdict(key, decodeProjectVerdict(raw), ttlMs); errors.delete(key);
							} catch (error) {
								removeCachedVerdict(key);
								const failure = { at: Date.now(), reason: "malformed verdict" };
								errors.set(key, failure); lastError.value = failure;
								logWarn("projectVerdicts: malformed verdict", { errorClass: projectVerdictFailure(error).errorClass });
							}
						}
					}
				} catch (error) {
					const affected = batch.filter(current);
					if (!affected.length) return;
					const diagnostic = projectVerdictFailure(error);
					if (error instanceof UnauthenticatedError || diagnostic.status === 401) {
						session.markSessionLost();
						return;
					}
					const failure = { at: Date.now(), reason: diagnostic.reason };
					const retryError = error as { retryAfterHeader?: unknown; retryAfter?: unknown } | null;
					const retryAfter = retryError?.retryAfterHeader ?? retryError?.retryAfter;
					let retryAfterMs: number | undefined;
					if (diagnostic.status === 429 && (typeof retryAfter === "number" || typeof retryAfter === "string" && retryAfter.trim())) {
						const value = String(retryAfter).trim();
						const delay = typeof retryAfter === "number" || /^\d+$/.test(value) ? Number(value) * 1000 : Date.parse(value) - Date.now();
						if (Number.isFinite(delay) && delay >= 0) retryAfterMs = Math.min(delay, 60000);
					}
					const transient = diagnostic.status === undefined || diagnostic.status === 408 || diagnostic.status === 429 || diagnostic.status >= 500;
					let shouldWarn = false;
					for (const id of affected) {
						const key = keyFor(id);
						if (!transient) removeCachedVerdict(key);
						errors.set(key, failure);
						if (transient) { failures.set(key, (failures.get(key) ?? 0) + 1); scheduleRetry(key, diagnostic.status === 429, retryAfterMs); }
						if (!warned.has(key)) { warned.add(key); shouldWarn = true; }
					}
					lastError.value = failure;
					if (shouldWarn) logWarn("projectVerdicts: verdict unavailable", { errorClass: diagnostic.errorClass });
				}
			})();
			for (const id of batch) inFlight.set(keyFor(id), request);
			waiting.push(request.finally(() => {
				for (const id of batch) if (inFlight.get(keyFor(id)) === request) inFlight.delete(keyFor(id));
			}));
		}
		await Promise.all(waiting);
	}
	function watchVisibleIds(ids: () => string[]): () => void {
		// Component-scoped watch: unchanged ids still react to expiry, invalidation and org clearing.
		// Transient errors retry only through the backoff timer; the watcher must not create a loop.
		return watch(() => ({ epoch: epoch.value, visible: ids().map((id) => ({ id, status: status(id) })) }), ({ visible }) => {
			const pending = visible.filter((entry) => entry.status === "missing" || entry.status === "stale").map((entry) => entry.id);
			if (pending.length) void ensure(pending);
		}, { immediate: true });
	}
	async function refresh(ids: string[], single = false): Promise<void> {
		for (const id of ids) {
			const key = keyFor(id);
			generations.set(key, (generations.get(key) ?? 0) + 1);
			// Restart backoff without removing the recovery UI while this request is pending.
			expired.add(key); inFlight.delete(key); cancelRetry(key);
		}
		await ensure(ids, single);
	}
	async function prefetch(id: string): Promise<ProjectAccessVerdict | undefined> {
		await ensure([id], true);
		return access(id).verdict;
	}
	async function refetch(ids: string[]): Promise<void> { invalidate(ids); await ensure(ids); }
	async function onForbidden(id: string): Promise<void> { await refetch([id]); }
	const scope = effectScope(true);
	scope.run(() => {
		// A verdict is a per-user answer: a different user or account must never inherit another user's cached grant, even in
		// the same organization, so identity changes clear the cache exactly like an organization switch or a lost session.
		watch(() => [session.organizationId, session.sessionLost, session.userId ?? null, session.accountId ?? null], clear, { flush: "sync" });
	});
	onScopeDispose(() => { scope.stop(); clear(); });
	return { entries, errors, lastError, ensure, watchVisibleIds, access, prefetch, refresh, verdict, status, refetch, invalidate, clear, onForbidden };
});
